import * as React from "react";
import { Tab, Tabs } from "@blueprintjs/core";
import { NextSeo } from "next-seo";
import Loading from "../../loading";
import PageHeader from "../../page-header";
import TaxonomyBreadcrumbs from "../../taxonomy-breadcrumbs";
import GenomesAnnotations from "../../genomes-annotations";
import { RelatedGenomes } from "../../related-genomes";
import AnnotationStore from "../../../stores/annotation-store";
import UnexpectedBackendError from "../../backend-error";
import { observer } from "mobx-react";
import { isServer, StoresContext } from "../../../pages/_app";

import Router from "next/router";
import { GenomeMetadata } from "../../genome-metadata";
import { FungalAnnotations } from "../../fungal-annotations";
import { ViralAnnotations } from "../../viral-annotations";
import { ProtistanAnnotations } from "../../protistan-annotations";
import ViralVariantStore from "../../../stores/viral-variant-store";
import { useContext, useEffect, useState } from "react";
import MicrobeDetailsQualityControl from "./qc";
import { GenomeHeader } from "../../genome-header";
import { getGenomeSeo } from "../../../utils/genomes";

enum TabIds {
  Overview = "overview-tab",
  Annotations = "annotations-tab",
  ViralVariants = "viral-variants",
  RelatedGenomes = "related-genomes-tab",
  QualityControl = "quality-control-tab",
}

const ALL_TABS: string[] = [TabIds.Overview, TabIds.RelatedGenomes, TabIds.QualityControl];

type Props = {
  uuid: string;
  tab: string | undefined;
  annotationStore?: AnnotationStore;
  viralVariantStore?: ViralVariantStore;
};

const MicrobeDetailsPage = (props: Props) => {
  const [tab, setTab] = useState<TabIds>(TabIds.Overview);
  const rootStore = useContext(StoresContext);
  const annotationStore = rootStore.annotationStore;

  const onPopState = () => {
    if (isServer) {
      return;
    }

    const tab = new URLSearchParams(window.location.search).get("tab") || "";
    if (ALL_TABS.indexOf(tab) >= 0 || tab === TabIds.Annotations || tab === TabIds.ViralVariants) {
      setTab(tab as TabIds);
    } else {
      // Setting the default tab to prevent a tab-less page
      setTab(TabIds.Overview);
    }
  };

  useEffect(() => {
    setTab(TabIds.Overview);

    (async () => {
      if (!annotationStore.genome || annotationStore.genome.uuid !== props.uuid) {
        await annotationStore.fetchGenomeDetails(props.uuid);
        rootStore.viralVariantStore.updateFromAnnotationStore(annotationStore);
      }

      const tabs = ALL_TABS.slice();
      tabs.push(
        annotationStore.genome?.collection_name === "virology"
          ? TabIds.ViralVariants
          : TabIds.Annotations
      );
      if (props.tab && tabs.indexOf(props.tab) >= 0) {
        setTab(props.tab as TabIds);
        if ((props.tab as TabIds) === TabIds.Annotations && !annotationStore.hasRightAnnotations) {
          await annotationStore.fetchAnnotations();
        }
      }
    })();

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [props.uuid]);

  const changeTab = (tab: TabIds) => {
    if (tab === TabIds.Annotations && !annotationStore.hasRightAnnotations) {
      annotationStore.fetchAnnotations();
    }

    setTab(tab);
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("tab", tab);
    const newUrl = `/genomes/${props.uuid}?${searchParams.toString()}`;
    Router.push(`/genomes/[uuid]`, newUrl, { shallow: true });
  };

  const hasErrored = annotationStore.error || rootStore.viralVariantStore.error;
  if (hasErrored) {
    return <UnexpectedBackendError />;
  }

  const { loadingGenomeDetails, genome, setUuids } = annotationStore;
  const loadingViralVariants = rootStore.viralVariantStore!.loadingGenomeDetails;

  if (loadingGenomeDetails || loadingViralVariants) {
    return <Loading text="Loading genome..." />;
  }

  if (!genome) {
    return <h3 style={{ textAlign: "center", marginTop: "200px" }}>Genome not found</h3>;
  }

  let annotationsTab;
  if (genome.collection_name === "virology") {
    const variantRuns = genome.primary_assembly?.variant_runs;
    if (variantRuns && variantRuns.length > 0) {
      // Temporarily restrict this behind a feature flag
      annotationsTab = (
        <Tab title="Genomic Variants" id={TabIds.ViralVariants} panel={<ViralAnnotations />} />
      );
    }
  } else if (setUuids.length === 0) {
    annotationsTab = null;
  } else if (genome.collection_name === "mycology") {
    annotationsTab = (
      <Tab title="Annotations" id={TabIds.Annotations} panel={<FungalAnnotations />} />
    );
  } else if (genome.collection_name === "protistology") {
    annotationsTab = (
      <Tab title="Annotations" id={TabIds.Annotations} panel={<ProtistanAnnotations />} />
    );
  } else {
    annotationsTab = (
      <Tab
        title="Genome Browser"
        id={TabIds.Annotations}
        panel={
          <GenomesAnnotations
            genomeName={genome.name}
            genomeUuid={genome.uuid}
            assemblyUuid={genome.primary_assembly?.uuid}
            collectionName={genome.collection_name}
          />
        }
      />
    );
  }

  return (
    <>
      <NextSeo {...getGenomeSeo(genome)} />
      <PageHeader name={<GenomeHeader genome={genome} />}>
        <TaxonomyBreadcrumbs taxon={genome.taxon} genome={genome} />
      </PageHeader>

      <div className="detail-navigation">
        <Tabs id="tabs" animate={false} selectedTabId={tab} onChange={changeTab}>
          <Tab title="Overview" id={TabIds.Overview} panel={<GenomeMetadata genome={genome} />} />
          {annotationsTab}
          <Tab
            title="Related Genomes"
            id={TabIds.RelatedGenomes}
            panel={<RelatedGenomes genome={genome} />}
          />
          <Tab
            title="Quality Control"
            id={TabIds.QualityControl}
            panel={
              <MicrobeDetailsQualityControl
                genome={genome}
                collectionName={genome.collection_name}
                notes={
                  genome.attributes!.atcc_metadata
                    ? genome.attributes!.atcc_metadata.notes || ""
                    : ""
                }
              />
            }
          />
        </Tabs>
      </div>
    </>
  );
};

export default observer(MicrobeDetailsPage);
